import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import {hasAiAccess, hasPermission, isClientUser} from '../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import {canOpenAuditLogReport} from './auditLogReport';
import {ReportGrain} from './reportGrain';

/** Gate for the AI report builder: both feature flags, INSIGHTS_READ, AI_ACCESS, and not a client user. Shared
 * by the chat entry point, the route, and the page so the gate lives in one place.
 *
 * The client-user exclusion mirrors `canUseAgentChat`, so the builder and the agent state one
 * entitlement rather than two. The other half of that rule — excluding collaborator seats — is not
 * applied here: it needs a viewer's profiles, and none of this gate's call sites has them. For the
 * chat route it is already enforced upstream, since the drawer renders nothing for a collaborator.
 * What remains reachable is a collaborator holding INSIGHTS_READ re-opening an existing AI report
 * from the reports list, which the backend now serves rather than failing. */
export function canOpenReportBuilder(): boolean {
	return (
		hasFeatureFlag('ai_report_builder') &&
		hasFeatureFlag('forecast_agent_chat') &&
		hasPermission(PERMISSION_TYPE.INSIGHTS_READ) &&
		hasAiAccess() &&
		!isClientUser()
	);
}

/**
 * Whether this viewer may open the builder surface for a report of this grain.
 *
 * The surface is shared, the entitlements are not. Every grain but the audit log is the AI report
 * builder's, and rides its two flags. The audit log is a compliance report the app authors itself,
 * behind its own flag and its own permissions -- see `auditLogReport.canOpenAuditLogReport`.
 *
 * This exists because the page had ONE gate, `canOpenReportBuilder()`, and the Reports list sent
 * audit reports to it. A controller holding only the audit flag was routed to `/report-builder/:id`
 * and immediately redirected home: the list said the report could be opened and the page disagreed,
 * with no message in between. A null grain -- a report type this builder does not build -- is
 * refused by the page on its own grounds, before this is asked.
 */
export function canOpenGrain(grain: ReportGrain | null): boolean {
	if (grain === 'TIME_REG_AUDIT_LOG') {
		return canOpenAuditLogReport();
	}
	return canOpenReportBuilder();
}

/** Whether the route should render at all. Deliberately the union: which grains this viewer may
 * actually see is decided per report by `canOpenGrain`, once the report is known. */
export function canReachReportBuilderRoute(): boolean {
	return canOpenReportBuilder() || canOpenAuditLogReport();
}
